import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useRef, useSyncExternalStore } from "react"
import { useQueryClient } from "@tanstack/react-query"
import FullCalendar from "@fullcalendar/react"
import { DateTime, Duration } from "luxon"
import { ConfigQuery } from "../../../createOrderApi"
import type { TimelineVisibleRange } from "../calendar/useTimelineVisibleRange"

const REFRESH_THROTTLE_MS = 10 * 1000
const MEETING_LEAD = Duration.fromObject({ hours: 1 })
const SIDE_TOLERANCE_MS = 60 * 1000

export type TimelineSide = "inView" | "before" | "after" | "unknown"

type TimeWindow = Readonly<{
    start: string
    end: string
}>

export type CalendarRefresher = (calendar: FullCalendar) => void

const RefreshCalendarContext = createContext<{
    setCalendarRef: (ref: FullCalendar | null) => void
    registerRefresher: (refresher: CalendarRefresher) => () => void
    triggerRefetch: () => void
    triggerThrottledRefetch: () => void
    goToMeeting: (start: DateTime) => void
    setVisibleRange: (range: TimelineVisibleRange | null) => void
    subscribeVisibleRange: (listener: () => void) => () => void
    getVisibleRange: () => TimelineVisibleRange | null
} | null>(null)

type Props = Readonly<PropsWithChildren<{}>>

export function RefreshCalendarProvider({ children }: Props) {
    const calendarRef = useRef<FullCalendar | null>(null)
    const refresherRef = useRef<CalendarRefresher | null>(null)
    const lastRefreshAtRef = useRef<number>(0)
    const visibleRangeRef = useRef<TimelineVisibleRange | null>(null)
    const visibleRangeListenersRef = useRef(new Set<() => void>())
    const queryClient = useQueryClient()

    const setCalendarRef = useCallback((ref: FullCalendar | null) => {
        calendarRef.current = ref
    }, [])

    const registerRefresher = useCallback((refresher: CalendarRefresher) => {
        refresherRef.current = refresher
        return () => {
            if (refresherRef.current === refresher) refresherRef.current = null
        }
    }, [])

    const triggerRefetch = useCallback(() => {
        lastRefreshAtRef.current = DateTime.utc().toMillis()
        // day queries come from fetchQuery and have no observers, so invalidating alone never refetches
        queryClient.invalidateQueries({ queryKey: [ConfigQuery.EVENTS_LIST_BY_DAY] })
        queryClient.invalidateQueries({ queryKey: [ConfigQuery.RESOURCES_FREE_BUSY] })

        const calendar = calendarRef.current
        if (!calendar) return

        if (refresherRef.current) refresherRef.current(calendar)
        else calendar.getApi().refetchEvents()
    }, [queryClient])

    const triggerThrottledRefetch = useCallback(() => {
        if (DateTime.utc().toMillis() - lastRefreshAtRef.current < REFRESH_THROTTLE_MS) return
        triggerRefetch()
    }, [triggerRefetch])

    const goToMeeting = useCallback((start: DateTime) => {
        const api = calendarRef.current?.getApi()
        if (!api) return

        const { activeStart, activeEnd } = api.view
        const viewDays = Math.round(DateTime.fromJSDate(activeEnd).diff(DateTime.fromJSDate(activeStart), "days").days)
        // the meeting day sits in the middle of the window, as the day navigation keeps it
        const dayOffset = Math.max(0, Math.floor((viewDays - 1) / 2))
        const windowStart = start.startOf("day").minus({ days: dayOffset })

        api.gotoDate(windowStart.toJSDate())
        api.scrollToTime(start.minus(MEETING_LEAD).diff(windowStart).toMillis())
    }, [])

    const setVisibleRange = useCallback((range: TimelineVisibleRange | null) => {
        visibleRangeRef.current = range
        visibleRangeListenersRef.current.forEach((listener) => listener())
    }, [])

    const subscribeVisibleRange = useCallback((listener: () => void) => {
        visibleRangeListenersRef.current.add(listener)
        return () => {
            visibleRangeListenersRef.current.delete(listener)
        }
    }, [])

    const getVisibleRange = useCallback(() => visibleRangeRef.current, [])

    const value = useMemo(
        () => ({
            setCalendarRef,
            registerRefresher,
            triggerRefetch,
            triggerThrottledRefetch,
            goToMeeting,
            setVisibleRange,
            subscribeVisibleRange,
            getVisibleRange,
        }),
        [
            setCalendarRef,
            registerRefresher,
            triggerRefetch,
            triggerThrottledRefetch,
            goToMeeting,
            setVisibleRange,
            subscribeVisibleRange,
            getVisibleRange,
        ],
    )

    return <RefreshCalendarContext.Provider value={value}>{children}</RefreshCalendarContext.Provider>
}

export function useRefreshCalendar() {
    const context = useContext(RefreshCalendarContext)

    // If the hook is used outside of the Provider, context will be null
    if (!context) {
        throw new Error(
            "useRefreshCalendar must be used within a RefreshCalendarProvider. " +
                "Check that you have wrapped your component " +
                "in <RefreshCalendarProvider ...>.",
        )
    }

    return context
}

// In view once as much of it shows as goToMeeting would leave on screen.
function sideOf(timeWindow: TimeWindow | null, range: TimelineVisibleRange | null): TimelineSide {
    if (!timeWindow || !range) return "unknown"

    const start = DateTime.fromISO(timeWindow.start).toMillis()
    const end = DateTime.fromISO(timeWindow.end).toMillis()
    if (!(end > start)) return "unknown"

    const shown = Math.min(end, range.end) - Math.max(start, range.start)
    const roomAfterLead = range.end - range.start - MEETING_LEAD.toMillis()
    if (shown >= Math.min(end - start, roomAfterLead) - SIDE_TOLERANCE_MS) return "inView"

    return start < range.start ? "before" : "after"
}

export function useTimelineSide(timeWindow: TimeWindow | null): TimelineSide {
    const { subscribeVisibleRange, getVisibleRange } = useRefreshCalendar()

    return useSyncExternalStore(subscribeVisibleRange, () => sideOf(timeWindow, getVisibleRange()))
}
